import { useEffect, useState } from "react"
import { Modal } from "common/Modal"
import UserService from "services/user.service"
import { useNotifications } from "hooks/useNotification"

const service = new UserService()
const INPUT_CLASS = "w-full px-4 py-2 font-light text-gray-800 border border-gray-200 rounded-md focus:border-gray-300 focus:outline-none focus:ring-0"

const IconTrash = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
)

type Props = {
    showModal: boolean
    setShowModal: (value: boolean) => void
    onSuccess: () => void
} & (
    | { operationId: string; tripId?: never }
    | { tripId: string; operationId?: never }
)

const initialForm = { files: [] as File[] }

export const AddExpenseModal = ({ showModal, setShowModal, onSuccess, ...ids }: Props) => {
    const { operationId, tripId } = ids

    const [form, setForm] = useState(initialForm)
    const [saving, setSaving] = useState(false)
    const { successMessage, errorMessage } = useNotifications()

    useEffect(() => {
        if (showModal) setForm(initialForm)
    }, [showModal])

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const fileArray = Array.from(e.target.files)
            setForm((prev) => ({ ...prev, files: [...prev.files, ...fileArray] }))
        }
    }

    const removeFile = (index: number) => {
        setForm((prev) => ({ ...prev, files: prev.files.filter((_, i) => i !== index) }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (form.files.length === 0) {
            errorMessage("Please add at least one receipt image")
            return
        }
        setSaving(true)
        try {
            const formData = new FormData()
            form.files.forEach((file) => formData.append("files", file))
            if (tripId) {
                await service.addExpenseToTrip(tripId, formData)
            } else if (operationId) {
                await service.addExpenseToOperation(operationId, formData)
            }
            successMessage("Expense added successfully")
            setForm(initialForm)
            setShowModal(false)
            onSuccess()
        } catch (error: any) {
            errorMessage(error?.response?.data?.message || "Failed to add expense")
            console.log(error)
        } finally {
            setSaving(false)
        }
    }

    const handleCancel = () => {
        setShowModal(false)
        setForm(initialForm)
    }

    return (
        <Modal
            showModal={showModal}
            setShowModal={setShowModal}
            title="Add Expense"
            width="w-full md:w-1/2"
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex flex-col gap-1.5">
                    <label className="field-label">
                        Receipt images
                        <span className="text-red-500"> *</span>
                    </label>
                    <input
                        type="file"
                        multiple
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleFileChange}
                        className={INPUT_CLASS}
                    />
                    {form.files.length > 0 && (
                        <div className="mt-2 space-y-2">
                            {form.files.map((file, index) => (
                                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                                    <span className="text-sm text-gray-700">{file.name}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeFile(index)}
                                        className="text-red-500 hover:text-red-700"
                                    >
                                        <IconTrash />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex gap-3 pt-4">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="flex-1 px-4 py-2 text-white bg-green-500 rounded-md hover:bg-green-600 disabled:bg-green-300 disabled:cursor-not-allowed transition-colors"
                    >
                        {saving ? "Adding..." : "Add Expense"}
                    </button>
                </div>
            </form>
        </Modal>
    )
}
